import axios, { AxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface FetchConfig extends AxiosRequestConfig {
  _retry?: boolean;
}

interface UseGlobalAxiosFetchReturn {
  isLoading: boolean;
  fetchData: (configParams?: FetchConfig) => Promise<AxiosResponse | AxiosError>;
}

const useGlobalAxiosFetch = (): UseGlobalAxiosFetchReturn => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const navigate = useNavigate();
  const useremail = localStorage.getItem('useremail');

  const refetchJwt = async (): Promise<boolean> => {
    if (!useremail) {
      // replace: 로그인 페이지를 보호 페이지 위에 쌓지 않는다 — 로그인 후 뒤로가기 시
      //  /login 재진입(앱은 requestRelogin 재발화로 로그인 드로어 재등장)을 막는다.
      navigate('/login', {
        replace: true,
        state: { showLoginRequiredAlert: true },
      });
      return false;
    }
    try {
      await axios.post(
        `${process.env.REACT_APP_API_SERVER}/refresh`,
        {},
        { withCredentials: true }
      );
      return true;
    } catch (e) {
      const error = e as AxiosError;
      const status = error?.response?.status || (error?.request as any)?.status;
      if (status === 401) {
        navigate('/login', {
          replace: true,
          state: { showLoginRequiredAlert: true },
        });
      } else {
        console.log(error);
      }
      return false;
    }
  };

  const fetchData = async (configParams?: FetchConfig): Promise<AxiosResponse | AxiosError> => {
    const axiosInstance = axios.create({
      baseURL: process.env.REACT_APP_API_SERVER,
      withCredentials: true,
    });

    setIsLoading(true);
    try {
      const response = await axiosInstance(configParams as AxiosRequestConfig);
      return response;
    } catch (error) {
      const axiosError = error as AxiosError;
      const status = axiosError?.response?.status || (axiosError?.request as any)?.status;
      if (status === 401) {
        if (configParams && configParams._retry) {
          navigate('/login', {
            replace: true,
            state: { showLoginRequiredAlert: true },
          });
          return axiosError.response as AxiosResponse;
        }

        const refreshed = await refetchJwt();
        if (!refreshed) {
          return axiosError.response as AxiosResponse;
        }

        const retryConfig = { ...(configParams || {}), _retry: true };
        return await axiosInstance(retryConfig as AxiosRequestConfig);
      } else {
        console.log(axiosError);
        return axiosError.response as AxiosResponse;
      }
    } finally {
      setIsLoading(false);
    }
  };

  return { isLoading, fetchData };
};

export default useGlobalAxiosFetch;
